import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { COLORS } from "../../../constants/design/colors";
import { useInstrumentResolver } from "../../../hooks/markets/useInstrumentResolver";
import { PositionHistoryItem } from "../../../interfaces/history";
import { getMarginTypeLabel } from "../../../utils/margin";
import {
  DetailRow,
  FormattedPrice,
  FormattedTime,
  SignedValue,
  SizeWithUsd,
} from "./DetailRow";
import HistoryDetailsShell from "./HistoryDetailsShell";

interface IPositionHistoryDetailsModalProps {
  position?: PositionHistoryItem;
  onHide?: () => void;
  style?: CSSProperties;
}

function PositionHistoryDetailsModal({
  position,
  onHide,
  style,
}: IPositionHistoryDetailsModalProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.details.PositionHistoryDetailsModal",
  });
  const { resolveInstrumentName, resolveMarketPrecision } =
    useInstrumentResolver();

  const instrumentName = position
    ? resolveInstrumentName(position as any) || position.instrument_name
    : undefined;
  const { amount_precision, price_precision } = position
    ? resolveMarketPrecision(position as any)
    : { amount_precision: 2, price_precision: 2 };

  if (!position) {
    return (
      <HistoryDetailsShell show={false} onHide={onHide} style={style}>
        <span />
      </HistoryDetailsShell>
    );
  }

  const isLong = position.position_side === "long";
  const sideLabel = position.is_liquidated
    ? isLong
      ? t("liquidated_long", { defaultValue: "Liquidated Long" })
      : t("liquidated_short", { defaultValue: "Liquidated Short" })
    : isLong
    ? t("long", { defaultValue: "Long" })
    : t("short", { defaultValue: "Short" });

  return (
    <HistoryDetailsShell
      show={!!position}
      onHide={onHide}
      instrumentName={instrumentName}
      // Pass the row's real leverage to the header. Only position history
      // carries a leverage field; pass `undefined` when absent so the header
      // hides it rather than showing a hardcoded `1x` (the shell's old default).
      leverage={
        position.leverage && Number(position.leverage) > 0
          ? Number(position.leverage)
          : undefined
      }
      copyId={{
        label: t("position_key", { defaultValue: "Position Key" }),
        value: position.position_key,
      }}
      style={style}
    >
      <DetailRow
        label={t("side", { defaultValue: "Side" })}
        value={
          <span
            style={{
              color: isLong ? COLORS.up.one : COLORS.down.one,
            }}
          >
            {sideLabel}
          </span>
        }
      />
      <DetailRow
        label={t("margin_type", { defaultValue: "Margin Type" })}
        value={getMarginTypeLabel(position.margin_type)}
      />
      <DetailRow
        label={t("leverage", { defaultValue: "Leverage" })}
        value={position.leverage ? `${position.leverage}x` : null}
      />
      <DetailRow
        label={t("fills", { defaultValue: "Fills" })}
        value={`${position.open_fills ?? 0} / ${position.close_fills ?? 0}`}
      />
      <DetailRow
        label={t("size", { defaultValue: "Size" })}
        value={
          <SizeWithUsd
            amount={position.amount}
            price={position.avg_entry_price}
            amountPrecision={amount_precision}
            pricePrecision={price_precision}
          />
        }
      />
      <DetailRow
        label={t("entry_price", { defaultValue: "Entry Price" })}
        value={
          <FormattedPrice
            value={position.avg_entry_price}
            precision={price_precision}
          />
        }
      />
      <DetailRow
        label={t("exit_price", { defaultValue: "Exit Price" })}
        value={
          <FormattedPrice
            value={position.avg_exit_price}
            precision={price_precision}
          />
        }
      />
      <DetailRow
        label={t("gross_pnl", { defaultValue: "Gross P&L" })}
        value={
          <SignedValue value={position.gross_pnl} precision={price_precision} />
        }
      />
      <DetailRow
        label={t("position_fee", { defaultValue: "Position Fee" })}
        value={
          <SignedValue
            value={position.position_fee}
            precision={price_precision}
            invert
          />
        }
      />
      <DetailRow
        label={t("borrowing_fee", { defaultValue: "Borrowing Fee" })}
        value={
          <SignedValue
            value={position.borrowing_fee}
            precision={price_precision}
            invert
          />
        }
      />
      <DetailRow
        label={t("funding_fee", { defaultValue: "Funding Fee" })}
        value={
          <SignedValue
            value={position.funding_fee}
            precision={price_precision}
          />
        }
      />
      <DetailRow
        label={t("total_fees", { defaultValue: "Total Fees" })}
        value={
          <SignedValue
            value={position.total_fees}
            precision={price_precision}
            invert
          />
        }
      />
      <DetailRow
        label={t("net_pnl", { defaultValue: "Net P&L" })}
        value={
          <SignedValue value={position.net_pnl} precision={price_precision} />
        }
      />
      <DetailRow
        label={t("opened", { defaultValue: "Opened" })}
        value={<FormattedTime value={position.opened_timestamp} unit="nanos" />}
      />
      <DetailRow
        label={t("closed", { defaultValue: "Closed" })}
        value={<FormattedTime value={position.closed_timestamp} unit="nanos" />}
      />
    </HistoryDetailsShell>
  );
}

export default PositionHistoryDetailsModal;
